import React, { useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface OTPInputProps {
    length?: number;
    value: string[];
    onChange: (otp: string[]) => void;
    onComplete?: (code: string) => void;
    error?: boolean;
    disabled?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
    length = 6,
    value,
    onChange,
    onComplete,
    error,
    disabled
}) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        // Auto focus first input on mount
        if (inputRefs.current[0] && !disabled) {
            inputRefs.current[0].focus();
        }
    }, [disabled]);

    const handleChange = (index: number, val: string) => {
        if (disabled) return;
        if (isNaN(Number(val))) return;

        const newOtp = [...value];
        // Take last character if multiple entered
        newOtp[index] = val.substring(val.length - 1);
        onChange(newOtp);

        // Auto advance
        if (val && index < length - 1 && inputRefs.current[index + 1]) {
            inputRefs.current[index + 1]?.focus();
        }

        // Check completion
        const code = newOtp.join('');
        // Logic from prototype: if this is last digit OR all digits filled
        if (code.length === length && newOtp.every(d => d !== '') && onComplete) {
            onComplete(code);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        // Backspace handling
        if (e.key === 'Backspace') {
            if (!value[index] && index > 0 && inputRefs.current[index - 1]) {
                // If empty, move back and delete previous
                const newOtp = [...value];
                newOtp[index - 1] = '';
                onChange(newOtp);
                inputRefs.current[index - 1]?.focus();
            } else {
                // Just clear current
                const newOtp = [...value];
                newOtp[index] = '';
                onChange(newOtp);
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        if (disabled) return;
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, length);

        if (!/^\d+$/.test(pastedData)) return; // Only numbers

        const digits = pastedData.split('');
        const newOtp = [...value];

        digits.forEach((digit, i) => {
            if (i < length) newOtp[i] = digit;
        });

        onChange(newOtp);

        // Focus appropriate input
        const focusIndex = Math.min(digits.length, length - 1);
        inputRefs.current[focusIndex]?.focus();

        if (digits.length === length && onComplete) {
            onComplete(pastedData);
        }
    };

    return (
        <div
            className="flex gap-2 sm:gap-3 justify-between w-full max-w-[400px] mx-auto"
            role="group"
            aria-label="Código de verificación de 6 dígitos"
        >
            {Array.from({ length }).map((_, index) => (
                <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    /* H08: autocomplete="one-time-code" en el primer campo —
                       los navegadores y gestores completan el OTP automáticamente */
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    aria-label={`Dígito ${index + 1} de ${length}`}
                    value={value[index] || ''}
                    onChange={e => handleChange(index, e.target.value)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={disabled}
                    className={cn(
                        "w-full h-14 sm:h-16 text-center text-xl font-bold rounded-input border bg-input-bg focus:outline-none transition-all duration-200",
                        // Active/Filled state
                        value[index]
                            ? "border-primary bg-white text-primary ring-1 ring-primary"
                            : "border-input-border hover:border-text-muted focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10",
                        // Error state
                        error && "!border-error !bg-red-50 !text-error !ring-error/20",
                        disabled && "opacity-50 cursor-not-allowed"
                    )}
                />
            ))}
        </div>
    );
};
